<template>
  <div id="forms">
    <client-only>
      <form-modal-v2
        v-if="activeForm && formattedActiveQuestions"
        :form="activeForm"
        :questions="formattedActiveQuestions"
        @close="handleCloseModal"
      />
    </client-only>
    <Breadcrumbs
      :crumbs="[
        { name: getGlobals.info.school_name, link: `${getPathPrefix}/` },
        { name: 'Forms', link: `${getPathPrefix}/forms` },
      ]"
    />
    <div
      ref="container"
      class="page-container"
    >
      <div class="forms-header">
        <h2 class="forms-title">
          {{ $t('forms.forms') }}
        </h2>
        <div
          v-if="showFormsList"
          class="forms-search"
        >
          <label
            for="forms-search-input"
            class="sr-only"
            >{{ $t('forms.search') }}</label
          >
          <text-input
            id="forms-search-input"
            v-model="search"
            type="search"
            :has-errors="false"
            :placeholder="$t('pages.shared.searchPlaceholder')"
            @update:model-value="debouncedSearch"
          />
          <img
            src="../../../assets/search.svg"
            class="search-icon"
            alt=""
          />
        </div>
      </div>
      <EmptyFormsList v-if="emptyFormsList" />
      <UnavailableForm
        v-else-if="unavailableForm"
        @close-unavailable="unavailableForm = false"
      />
      <FormsList
        v-else
        :forms="forms"
        @open="handleOpenFormModal($event, 'click')"
        @open-via-keyboard="handleOpenFormModal($event, 'keyboard')"
      />
      <p
        aria-live="polite"
        class="sr-only"
      >
        {{ ariaLiveFormCount }} {{ $t('forms.formsFound') }}
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import concat from 'lodash/concat';
import camelCase from 'lodash/camelCase';
import isPlainObject from 'lodash/isPlainObject';
import JsonApiResponseConverter from 'json-api-response-converter';
import FormModalV2 from '../../../components/pages/forms/FormModalV2.vue';
import Breadcrumbs from '../../../components/global/Breadcrumbs.vue';
import TextInput from '../../../components/global/Forms/TextInput.vue';
import UnavailableForm from '../../../components/pages/forms/views/UnavailableForm.vue';
import FormsList from '../../../components/pages/forms/views/FormsList.vue';
import EmptyFormsList from '../../../components/pages/forms/views/EmptyFormsList.vue';

export default defineNuxtComponent({
  name: 'Forms',
  components: {
    Breadcrumbs,
    TextInput,
    FormModalV2,
    UnavailableForm,
    FormsList,
    EmptyFormsList,
  },
  async asyncData({ $store, $axios }) {
    try {
      const locale = $store.getters['translation/getLocale'];
      const featureFlags = $store.state.featureFlags || {};

      const hasFormsEnabled = featureFlags.forms_service_enabled || featureFlags.vue_migration_forms_enabled;

      if (!hasFormsEnabled) {
        throw createError({ statusCode: 404, message: 'Page not found' });
      }

      const orgParam = {
        secondary_organization_id: $store.getters.getSecondaryOrgId,
      };
      const displayParams = {
        sort: 'name',
        scope: 'visible_on_school_website',
      };

      // using an axios instance causes generated pagination links to be built with incorrect domains
      const response = await axios.get(`${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/forms`, {
        params: {
          locale: locale !== 'en' ? locale : undefined,
          ...displayParams,
          ...orgParam,
        },
      });

      const unfilteredForms = response.data.data;
      const unfilteredPaginationLinks = response.data.links;
      let unavailableForm = false;
      const emptyFormsList = response.data.data.length === 0;
      let queryForm = null;
      let queryQuestions = '';
      let queryChoices = [];
      const formId = useRoute().params.id || false;
      const formUuid = useRoute().params.uuid || '';

      if (formId) {
        queryForm = unfilteredForms.find((form) => form.id === formId);
        if (!queryForm) {
          try {
            const {
              data: { data: directForm },
            } = await $axios.get(`${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/forms/${formId}/${formUuid}`, {
              params: {
                locale: locale !== 'en' ? locale : undefined,
                ...orgParam,
              },
            });
            if (directForm.attributes.status === 'SURVEY_STATUS_ACTIVE') {
              queryForm = directForm;
            } else {
              throw new Error('Form not currently active');
            }
          } catch (err) {
            unavailableForm = true;
            console.error(err);
          }
        }

        if (queryForm) {
          const questionsResponse = await $axios.get(`${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/forms/${queryForm.id}/elements`, {
            params: {
              locale: locale !== 'en' ? locale : undefined,
              include: 'element_choices,assets',
              ...orgParam,
            },
          });
          queryQuestions = new JsonApiResponseConverter(questionsResponse.data).formattedResponse;

          queryChoices = queryQuestions.reduce(
            (map, { element_choices, id }) => ({
              ...map,
              [id]: element_choices,
            }),
            {},
          );
        }
      }

      return {
        unfilteredForms,
        orgParam,
        displayParams,
        unfilteredPaginationLinks,
        activeForm: queryForm,
        activeQuestions: queryQuestions,
        activeChoices: queryChoices,
        unavailableForm,
        emptyFormsList,
      };
    } catch (e) {
      console.error(e);
      throw createError({
        statusCode: 500,
        message: 'Something went wrong',
      });
    }
  },
  data() {
    return {
      forms: [],
      search: '',
      paginationLinks: {},
      scrollTop: 0,
      loading: false,
      debouncedSearch: debounce(this.handleSearch, 500),
    };
  },
  setup() {
    useHead({
      title: 'Forms',
    });
  },
  computed: {
    ...mapGetters(['getSecondaryOrgId', 'getGlobals', 'getPathPrefix', 'getThemeHeadingColor']),
    ...mapGetters('translation', ['getLanguage', 'getLocale']),
    ariaLiveFormCount() {
      return get(this.forms, 'length');
    },
    hasSearch() {
      return this.search && this.search.trim().length > 0;
    },
    showFormsList() {
      return !this.unavailableForm && !this.emptyFormsList;
    },
    formattedActiveQuestions() {
      if (!Array.isArray(this.activeQuestions)) return this.activeQuestions;
      return this.activeQuestions.map((question) => ({
        ...this.camelCaseKeys(question),
      }));
    },
  },
  created() {
    this.forms = this.unfilteredForms;
    this.paginationLinks = this.unfilteredPaginationLinks;
  },
  mounted() {
    document.addEventListener('scroll', this.checkForAddingForms);
  },
  beforeUnmount() {
    if (import.meta.client) {
      document.removeEventListener('scroll', this.checkForAddingForms);
    }
  },
  methods: {
    async handleSearch(value) {
      let searchResults;
      if (this.hasSearch) {
        searchResults = await this.getSearchResults(value);
      }
      this.forms = !this.hasSearch ? this.unfilteredForms : searchResults.data;
      this.paginationLinks = !this.hasSearch ? this.unfilteredPaginationLinks : searchResults.links;
    },
    async getSearchResults(query) {
      const response = await this.$axios.get(`${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/forms`, {
        params: {
          locale: this.getLocale !== 'en' ? this.getLocale : undefined,
          ...this.displayParams,
          ...this.orgParam,
          query,
        },
      });
      return response.data;
    },
    checkForAddingForms() {
      this.scrollTop = window.pageYOffset;
      const container = this.$refs.container;
      const { clientHeight } = document.documentElement;
      if (this.scrollTop + clientHeight > container.clientHeight) {
        this.addMoreForms();
      }
    },
    async addMoreForms() {
      if (!this.paginationLinks.next) return;
      try {
        if (this.loading) return;
        this.loading = true;
        const { data } = await this.$axios.get(`${this.paginationLinks.next}`);
        this.loading = false;
        const nextFormsPage = data.data;
        if (this.hasSearch) {
          this.paginationLinks = data.links;
          this.forms = concat(this.forms, nextFormsPage);
        } else {
          this.unfilteredPaginationLinks = data.links;
          this.paginationLinks = this.unfilteredPaginationLinks;
          this.unfilteredForms = concat(this.unfilteredForms, nextFormsPage);
          this.forms = this.unfilteredForms;
        }
      } catch (e) {
        console.error(e);
      }
    },
    async handleOpenFormModal(form, actionType) {
      try {
        const questionsResponse = await this.$axios.get(
          `${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/forms/${form.id}/elements`,
          {
            params: {
              locale: this.getLocale !== 'en' ? this.getLocale : undefined,
              include: 'element_choices',
              secondary_organization_id: this.getSecondaryOrgId,
            },
          },
        );

        this.activeQuestions = new JsonApiResponseConverter(questionsResponse.data).formattedResponse;

        this.activeChoices = this.activeQuestions.reduce(
          (map, { element_choices, id }) => ({
            ...map,
            [id]: element_choices,
          }),
          {},
        );
        this.activeForm = { ...form, openedBy: actionType };

        // return navigateTo causes a rerender and makes the form modal opening jump.
        history.pushState(null, null, `${this.getPathPrefix}/forms/${form.id}`);
      } catch (err) {
        console.error(err);
      }
    },
    handleCloseModal() {
      document.querySelector('body').style.overflow = 'visible';
      this.formReset();
      return navigateTo({ path: `${this.getPathPrefix}/forms` });
    },
    formReset() {
      this.activeForm = null;
      this.activeQuestions = '';
      this.activeChoices = [];
    },
    camelCaseKeys(object) {
      return Object.entries(object).reduce((obj, [key, value]) => {
        let localValue = value;
        if (isPlainObject(value)) {
          localValue = this.camelCaseKeys(value);
        }
        return { ...obj, [camelCase(key)]: localValue };
      }, {});
    },
  },
});
</script>

<style scoped lang="scss">
#forms {
  .forms-header {
    display: flex;
    justify-content: space-between;
    margin: 0 auto 36px;
    width: 90%;
  }
  .forms-search {
    position: relative;
    width: clamp(182px, 70%, 250px);
    height: 40px;
    margin-left: 5px;
    :deep(.form-input) {
      input {
        box-sizing: border-box;
        border-radius: 4px 4px 0 0;
      }
    }
    .search-icon {
      position: absolute;
      top: 5px;
      right: 12px;
    }
  }
}
.sr-only {
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
}
</style>
